import {
  AUFGABEN_PRO_RUNDE,
  KARTEN_PRO_RUNDE,
  ZEITSCHWELLEN,
  FACH_INTERVALLE,
  MAX_FACH,
  RUECKSTUFUNG_BEI_HAKT,
  ERSTKONTAKT_FACH,
  WIEDERVORLAGE_ABSTAND_MIN,
  WIEDERVORLAGE_ABSTAND_MAX,
  WIEDERVORLAGEN_PRO_KARTE,
  BALLON_ANTEIL,
  MULTIPLIKATION_GEWICHT,
  DIVISION_GEWICHT,
  KETTE_AB,
  PAUSE_SCHWELLE_MS,
} from '../config'
import { alleKarten, karteFuer, erlaubteVarianten, ballonAntworten } from '../karten/karten'
import type { KartenId, Variante, Zufall } from '../karten/typen'
import { plusTage, istFaellig } from './datum'
import type {
  Aufgabe,
  Antwortprotokoll,
  Format,
  IsoDatum,
  Kartenstand,
  Rueckmeldung,
  Rundenergebnis,
  Zone,
} from './typen'

/**
 * In welche der drei Zonen eine Antwort faellt.
 *
 * Der Kern der ganzen App. Nicht "richtig oder falsch" steuert die
 * Wiedervorlage, sondern "abgerufen, ausgerechnet oder gar nicht da". Eine
 * richtige Antwort nach 12 Sekunden ist fuer das Lernziel dasselbe wie eine
 * falsche: Die Zahl war nicht verfuegbar, sie wurde hergeleitet.
 */
export function zoneFuer(richtig: boolean, zeitMs: number, format: Format): Zone {
  if (!richtig) return 'hakt'
  const schwelle = ZEITSCHWELLEN[format]
  if (zeitMs < schwelle.gewusst) return 'gewusst'
  if (zeitMs < schwelle.gerechnet) return 'gerechnet'
  return 'hakt'
}

/**
 * War das eine Denkpause oder eine Unterbrechung?
 *
 * Die Antwort entscheidet, ob eine Antwort ueberhaupt gewertet wird. Wird
 * Lina beim Ueben gerufen, legt das Tablet weg und tippt zehn Minuten spaeter
 * die richtige Zahl, dann hat sie die Aufgabe gewusst. Ohne diese Unterscheidung
 * faende der Scheduler dort eine Antwortzeit von zehn Minuten vor und wuerde
 * eine sicher sitzende Karte zwei Faecher zurueckstufen.
 *
 * Solche Antworten werden deshalb verworfen und die Aufgabe erneut gestellt.
 * Lieber ein fehlender Messwert als ein falscher.
 */
export function istUnterbrechung(zeitMs: number): boolean {
  return zeitMs > PAUSE_SCHWELLE_MS
}

export function neuerStand(karte: KartenId, heute: IsoDatum): Kartenstand {
  return {
    karte,
    fach: 0,
    faelligAm: heute,
    letzteZeitMs: 0,
    anzahlGewusst: 0,
    anzahlGerechnet: 0,
    anzahlHakt: 0,
    erstkontakt: true,
  }
}

export function alleStaendeNeu(heute: IsoDatum): Kartenstand[] {
  return alleKarten().map((k) => neuerStand(k.id, heute))
}

/** Der Fachwechsel und die neue Faelligkeit nach einer Antwort. */
export function naechsterStand(
  stand: Kartenstand,
  zone: Zone,
  zeitMs: number,
  heute: IsoDatum,
): Kartenstand {
  let fach = stand.fach

  if (zone === 'gewusst') {
    // Erstkontakt-Sprung: Wer eine Karte auf Anhieb abruft, muss sich nicht
    // erst Fach fuer Fach hocharbeiten. Ohne das braeuchte die Einstufung
    // Wochen, weil Lina den Grossteil bereits kann.
    fach = stand.erstkontakt
      ? Math.max(ERSTKONTAKT_FACH, fach)
      : Math.min(MAX_FACH, fach + 1)
  } else if (zone === 'hakt') {
    fach = Math.max(0, fach - RUECKSTUFUNG_BEI_HAKT)
  }
  // "gerechnet" laesst das Fach stehen: Sie kann es, aber noch nicht schnell.
  // Die Karte kommt zum gleichen Abstand wieder, statt auf- oder abzusteigen.

  const intervall = FACH_INTERVALLE[fach] ?? 0

  return {
    ...stand,
    fach,
    faelligAm: zone === 'hakt' ? heute : plusTage(heute, intervall),
    letzteZeitMs: zeitMs,
    anzahlGewusst: stand.anzahlGewusst + (zone === 'gewusst' ? 1 : 0),
    anzahlGerechnet: stand.anzahlGerechnet + (zone === 'gerechnet' ? 1 : 0),
    anzahlHakt: stand.anzahlHakt + (zone === 'hakt' ? 1 : 0),
    erstkontakt: false,
  }
}

function mischen<T>(liste: T[], zufall: Zufall): T[] {
  const kopie = [...liste]
  for (let i = kopie.length - 1; i > 0; i--) {
    const j = Math.floor(zufall() * (i + 1))
    const a = kopie[i]
    const b = kopie[j]
    if (a !== undefined && b !== undefined) {
      kopie[i] = b
      kopie[j] = a
    }
  }
  return kopie
}

/**
 * Welche Karten in dieser Runde drankommen.
 *
 * Hoechstens 12 verschiedene auf 20 Aufgaben. Bei 20 verschiedenen Karten
 * gaebe es innerhalb der Runde keine einzige Wiederholung, und genau die
 * Wiederholung in der Sitzung ist der Mechanismus, der kurzfristig festigt.
 *
 * Ist nichts faellig, wird mit den am naechsten faellig werdenden Karten
 * aufgefuellt. Ein Tag ohne Runde darf nie am Scheduler liegen: Sonst risse
 * Linas Serie, obwohl sie da war.
 */
export function rundenKarten(
  staende: Kartenstand[],
  heute: IsoDatum,
  zufall: Zufall,
): Kartenstand[] {
  // Vorher mischen, danach stabil sortieren: So bleibt bei gleichem Fach und
  // gleicher Faelligkeit die Reihenfolge zufaellig. Ohne das bekaeme Lina am
  // ersten Tag immer 1x1 bis 2x3, weil dann alle 55 Karten gleichauf liegen.
  const gemischt = mischen(staende, zufall)

  const faellig = gemischt
    .filter((s) => istFaellig(s.faelligAm, heute))
    .sort((a, b) => a.fach - b.fach || a.faelligAm.localeCompare(b.faelligAm))

  if (faellig.length >= KARTEN_PRO_RUNDE) {
    return faellig.slice(0, KARTEN_PRO_RUNDE)
  }

  const rest = gemischt
    .filter((s) => !istFaellig(s.faelligAm, heute))
    .sort((a, b) => a.faelligAm.localeCompare(b.faelligAm) || a.fach - b.fach)

  return [...faellig, ...rest].slice(0, KARTEN_PRO_RUNDE)
}

interface OffeneWiedervorlage {
  variante: Variante
  format: Format
  /** Ab welcher Aufgabennummer sie wieder gestellt werden darf. */
  abNummer: number
}

/**
 * Eine Uebungsrunde.
 *
 * Haelt den Zustand einer laufenden Runde: was schon gestellt wurde, was
 * wiedervorgelegt werden muss, wie lang die Kette ist. Alles Persistente
 * kommt am Ende ueber `ergebnis()` heraus, die Runde selbst schreibt nichts.
 *
 * Die Schnittstelle nach aussen ist absichtlich schmal: naechsteAufgabe,
 * antwortMelden, ergebnis. Die Spielformate wissen nichts von Faechern und
 * Faelligkeiten, der Scheduler nichts von Ballons.
 */
export class Runde {
  private readonly staende = new Map<KartenId, Kartenstand>()
  /**
   * Das Fach beim Start der Runde, eingefroren.
   *
   * Die Variantenwahl richtet sich danach, nicht nach dem laufend
   * aktualisierten Fach. Sonst bekaeme eine Karte, die gerade eben zum ersten
   * Mal gewusst wurde und dadurch auf Fach 2 gesprungen ist, in derselben
   * Runde sofort die Umkehraufgabe vorgesetzt. Einmal abgerufen ist aber noch
   * keine Beherrschung, und die Division soll auf gesicherter Multiplikation
   * aufbauen.
   */
  private readonly fachBeiStart = new Map<KartenId, number>()
  private readonly heute: IsoDatum
  private readonly zufall: Zufall
  /** Gesetzt im Ausritt: Dann ist jede Aufgabe eine Ritt-Aufgabe. */
  private readonly festesFormat: Format | null

  private reihenfolge: KartenId[]
  private position = 0

  private gestellt = 0
  private aktuelle: Aufgabe | null = null

  private readonly wiedervorlagen: OffeneWiedervorlage[] = []
  private readonly wiedervorlagenGeplant = new Map<KartenId, number>()

  private readonly protokoll: Antwortprotokoll[] = []
  private readonly beruehrt = new Set<KartenId>()

  private kette = 0
  private laengsteKette = 0

  constructor(staende: Kartenstand[], heute: IsoDatum, zufall: Zufall, festesFormat?: Format) {
    for (const s of staende) {
      this.staende.set(s.karte, s)
      this.fachBeiStart.set(s.karte, s.fach)
    }
    this.heute = heute
    this.zufall = zufall
    this.festesFormat = festesFormat ?? null

    const plan = rundenKarten(staende, heute, zufall)
    this.reihenfolge = mischen(
      plan.map((s) => s.karte),
      zufall,
    )
  }

  /**
   * Die naechste Aufgabe, oder null wenn die Runde durch ist.
   *
   * Mehrfach aufrufbar ohne Nebenwirkung: Solange nicht geantwortet wurde,
   * kommt dieselbe Aufgabe zurueck. Sonst wuerde ein Neuzeichnen der
   * Oberflaeche die Aufgabe unter dem Kind wegziehen.
   */
  naechsteAufgabe(): Aufgabe | null {
    if (this.gestellt >= AUFGABEN_PRO_RUNDE) return null
    if (this.aktuelle) return this.aktuelle

    const nummer = this.gestellt + 1
    const faellige = this.wiedervorlagen.findIndex((w) => w.abNummer <= nummer)

    if (faellige >= 0) {
      const w = this.wiedervorlagen.splice(faellige, 1)[0]
      if (w) {
        this.aktuelle = this.baueAufgabe(w.variante, w.format, nummer, true)
        return this.aktuelle
      }
    }

    const karte = this.naechsteKarte()
    const variante = this.waehleVariante(karte, this.fachBeiStart.get(karte) ?? 0)
    const format: Format =
      this.festesFormat ?? (this.zufall() < BALLON_ANTEIL ? 'ballons' : 'zahlenfeld')

    this.aktuelle = this.baueAufgabe(variante, format, nummer, false)
    return this.aktuelle
  }

  private naechsteKarte(): KartenId {
    if (this.position >= this.reihenfolge.length) {
      // Durchlauf beendet. Neu mischen statt stumpf von vorn: Sonst kaeme
      // die zweite Haelfte der Runde in exakt derselben Abfolge wie die erste.
      this.reihenfolge = mischen(this.reihenfolge, this.zufall)
      this.position = 0
    }
    const karte = this.reihenfolge[this.position] ?? this.reihenfolge[0]
    this.position++
    // Die Liste ist nie leer: rundenKarten liefert immer mindestens eine Karte,
    // solange ueberhaupt Staende uebergeben wurden.
    return karte as KartenId
  }

  private waehleVariante(karte: KartenId, fach: number): Variante {
    const erlaubt = erlaubteVarianten(karteFuer(karte), fach)
    const mal = erlaubt.filter((v) => v.typ === 'mal')
    const geteilt = erlaubt.filter((v) => v.typ === 'geteilt')

    let gruppe = mal
    if (geteilt.length > 0 && mal.length > 0) {
      const schwelle = MULTIPLIKATION_GEWICHT / (MULTIPLIKATION_GEWICHT + DIVISION_GEWICHT)
      gruppe = this.zufall() < schwelle ? mal : geteilt
    } else if (mal.length === 0) {
      gruppe = geteilt
    }

    const index = Math.floor(this.zufall() * gruppe.length)
    return gruppe[index] ?? gruppe[0] ?? erlaubt[0]!
  }

  private baueAufgabe(
    variante: Variante,
    format: Format,
    nummer: number,
    istWiedervorlage: boolean,
  ): Aufgabe {
    return {
      variante,
      format,
      antworten: format === 'zahlenfeld' ? null : ballonAntworten(variante, this.zufall),
      nummer,
      gesamt: AUFGABEN_PRO_RUNDE,
      istWiedervorlage,
    }
  }

  antwortMelden(antwort: { eingabe: number; zeitMs: number }): Rueckmeldung {
    const aufgabe = this.aktuelle
    if (!aufgabe) {
      throw new Error('Es ist keine Aufgabe offen. Erst naechsteAufgabe aufrufen.')
    }

    const richtig = antwort.eingabe === aufgabe.variante.ergebnis
    const zone = zoneFuer(richtig, antwort.zeitMs, aufgabe.format)
    const karte = aufgabe.variante.karte

    const alt = this.staende.get(karte) ?? neuerStand(karte, this.heute)
    this.staende.set(karte, naechsterStand(alt, zone, antwort.zeitMs, this.heute))
    this.beruehrt.add(karte)

    this.protokoll.push({
      karte,
      variante: aufgabe.variante.id,
      format: aufgabe.format,
      richtig,
      zeitMs: antwort.zeitMs,
      zone,
    })

    this.kette = richtig && zone !== 'hakt' ? this.kette + 1 : 0
    this.laengsteKette = Math.max(this.laengsteKette, this.kette)

    if (zone === 'hakt') this.planeWiedervorlage(aufgabe)

    this.gestellt++
    this.aktuelle = null

    return {
      richtig,
      zone,
      loesung: aufgabe.variante.ergebnis,
      // Der Merksatz kommt auch bei einer richtigen, aber sehr langsamen
      // Antwort. Genau dort hilft er: Sie hat es hergeleitet, ihr fehlt nur
      // der kurze Weg.
      merksatz: zone === 'hakt' ? karteFuer(karte).merksatz : null,
      kette: this.kette,
      ketteGezuendet: this.kette > 0 && this.kette % KETTE_AB === 0,
      rundeFertig: this.gestellt >= AUFGABEN_PRO_RUNDE,
    }
  }

  private planeWiedervorlage(aufgabe: Aufgabe): void {
    const karte = aufgabe.variante.karte
    const geplant = this.wiedervorlagenGeplant.get(karte) ?? 0
    // Deckel pro Karte, sonst belegt eine einzige zaehe Karte die halbe Runde.
    if (geplant >= WIEDERVORLAGEN_PRO_KARTE) return

    const spanne = WIEDERVORLAGE_ABSTAND_MAX - WIEDERVORLAGE_ABSTAND_MIN + 1
    const abstand = WIEDERVORLAGE_ABSTAND_MIN + Math.floor(this.zufall() * spanne)

    this.wiedervorlagen.push({
      // Dieselbe Variante wie beim Fehlversuch. Sie soll genau die Aufgabe
      // einmal richtig tippen, an der sie gescheitert ist, nicht eine andere.
      variante: aufgabe.variante,
      format: aufgabe.format,
      abNummer: this.gestellt + 1 + abstand,
    })
    this.wiedervorlagenGeplant.set(karte, geplant + 1)
  }

  ergebnis(): Rundenergebnis {
    const richtige = this.protokoll.filter((p) => p.richtig)
    return {
      staende: [...this.beruehrt].map((k) => this.staende.get(k)!),
      protokoll: [...this.protokoll],
      anzahlAufgaben: this.gestellt,
      anzahlGewusst: this.protokoll.filter((p) => p.zone === 'gewusst').length,
      schnellsteMs: richtige.length ? Math.min(...richtige.map((p) => p.zeitMs)) : null,
      laengsteKette: this.laengsteKette,
    }
  }
}
